<cs-header>
  <cs-header-title>{{ LL.equipmentScanner.title() }}</cs-header-title>
</cs-header>

<cs-content>
  @if (cameraPermissionDismissed) {
    <cs-scanner-permission-denied [message]="LL.equipmentScanner.cameraPermissionDenied()" [scanner]="ScannerName.Equipment"></cs-scanner-permission-denied>
  }

  <!-- Dev-only MOCK button (gated on settings.testMode) — shown regardless of camera permission
       so testers can exercise the review-screen flow in a browser without camera access. -->
  @if (showMockButton && phase === 'camera') {
  <div class="dev-mock-bar">
    <cs-button secondary small [label]="'MOCK (dev only)' | untranslated" (click)="mockAnalyze()"></cs-button>
  </div>
  }

  <!-- Phase 1: Camera -->
  @if (!cameraPermissionDismissed && (phase === 'camera' || phase === 'analyzing')) {
    <!-- Center section: camera preview, or collage when all photos taken / during analysis -->
    <cs-scanner-camera-stage #stage
      [photos]="capturedPhotos"
      [analyzing]="analyzing"
      [progress]="analyzeProgress"
      [statusText]="analyzeStatusText"
      [retryMessage]="retryMessage"
      [suppressAnalyzingOverlay]="true"
      (cameraReady)="onCameraStateChange()"
      (cameraError)="onCameraError($event)"
      (permissionChange)="onCameraStateChange()">
    </cs-scanner-camera-stage>

    <!-- Bottom section: photo reel + hints + shutter bar while capturing; once
         analysis starts that is replaced by a reserved bottom bar holding the
         progress, so it sits below the photo instead of overlapping it -->
    @if (!analyzing) {
      <cs-scanner-photo-reel #reel
        [photos]="capturedPhotos"
        [maxSlots]="MAX_EQUIPMENT_SCAN_IMAGES"
        [canAdd]="!stage.cameraView?.isActive"
        (remove)="removePhotoAt($event)"
        (inspect)="inspectPhotoAt($event)"
        (add)="addPhoto()">
      </cs-scanner-photo-reel>

      <cs-scanner-hints [hints]="photoHints"></cs-scanner-hints>

      <!-- Shutter bar: upload left, shutter center, Analyze right -->
      <cs-scanner-shutter-bar
        [shutterHidden]="!stage.cameraView?.isActive"
        [shutterDisabled]="allPhotosTaken"
        [nativeShutterSound]="stage.cameraView?.isNative"
        (snap)="snapPhoto()">
        <div left class="left-buttons">
          <cs-button icon="add" secondary (click)="openFilePicker()" [disabled]="allPhotosTaken"></cs-button>
          @if (modelSelection.scanMode === 'any') {
            <cs-scanner-model-picker
              [modelOptions]="modelSelection.modelOptions"
              [selectedModel]="modelSelection.selectedModel"
              (modelSelected)="onModelSelected($event)">
            </cs-scanner-model-picker>
          }
        </div>
        <div right>
          <!-- once all photos are taken, the big Analyze/Retake page buttons below take over -->
          @if (hasPhotos && !allPhotosTaken) {
            <cs-button primary small [label]="LL.equipmentScanner.analyzeButton()" (click)="analyzeAsync()"></cs-button>
          }
        </div>
      </cs-scanner-shutter-bar>
    } @else {
      <div class="scanner-bottom-bar">
        <cs-scanner-analyzing-overlay
          [statusText]="analyzeStatusText"
          [progress]="analyzeProgress"
          [retryMessage]="retryMessage">
        </cs-scanner-analyzing-overlay>
      </div>
    }
  }

  <!-- Results -->
  @if (phase === 'results' && scanResult) {
    <cs-card-list>
    
      <!-- 1. Detected summary card: type + suggested name + unit + type-mismatch banner.
           Layout mirrors equipment-edit page Details section so the user sees a familiar shape. -->
      <cs-card class="scan-summary">
        <cs-card-content>
          <cs-item-group>
            <div class="details-row">
              <cs-form-field class="equipment-type-field" [label]="LL.equipment.labels.equipmentType()">
                <cs-select [value]="selectedKey" (selectionChange)="onTypeOverride($event)" [disabled]="isTypeLocked"
                  [placeholder]="LL.equipmentScanner.types.unknown()">
                  <cs-select-option value="barbell" [label]="LL.equipmentScanner.types.barbell()"></cs-select-option>
                  <cs-select-option value="dumbbells" [label]="LL.equipmentScanner.types.dumbbells()"></cs-select-option>
                  <cs-select-option value="plates" [label]="LL.equipmentScanner.types.plates()"></cs-select-option>
                  <cs-select-option value="kettlebells" [label]="LL.equipmentScanner.types.kettlebells()"></cs-select-option>
                  <cs-select-option value="cable-machine" [label]="LL.equipmentScanner.types.cableMachine()"></cs-select-option>
                </cs-select>
              </cs-form-field>
              @if (suggestedName) {
                <cs-form-field flex-width class="name-field suggested-name-field" [class.dimmed]="!nameAccepted">
                  <cs-label class="suggested-name-label">
                    <span>{{ LL.equipment.labels.name() }}</span>
                    <cs-checkbox [(checked)]="nameAccepted"></cs-checkbox>
                  </cs-label>
                  <cs-input [(value)]="suggestedName" [disabled]="!nameAccepted"
                    [placeholder]="LL.equipment.placeholders.enterName()" autocomplete="off" />
                </cs-form-field>
              }
            </div>
            <!-- kg/lb are unit symbols, not translatable prose -->
            <cs-form-field fit-width class="units-field" [label]="LL.equipment.labels.units()">
              <cs-select [(value)]="selectedUnit" (selectionChange)="onUnitToggle()">
                <cs-select-option value="kg" [label]="'kg' | untranslated"></cs-select-option>
                <cs-select-option value="lb" [label]="'lb' | untranslated"></cs-select-option>
              </cs-select>
            </cs-form-field>
            @if (showTypeMismatchBanner) {
              <cs-text-alert type="warning" [message]="typeMismatchMessage"></cs-text-alert>
            }
          </cs-item-group>
        </cs-card-content>
      </cs-card>

      <!-- 2. Detected items card -->
      <cs-card class="detected-card">
        <cs-card-title>{{ LL.equipmentScanner.detectedHeader({ count: resultItems.length }) }}</cs-card-title>
        <cs-card-content>
          @if (displayType === EquipmentType.Bar && detectedBarRows) {
          <cs-item-list>
              @if (detectedBarRows.bar) {
                <cs-form-field [label]="LL.equipmentScanner.barGroup()">
                  <cs-table class="detected-table">
                    <cs-table-row>
                      <cs-table-cell><cs-checkbox [ariaLabel]="detectedBarRows.bar.display | untranslated"
                        [(checked)]="detectedBarRows.bar.checked"></cs-checkbox></cs-table-cell>
                      <cs-table-cell>{{ detectedBarRows.bar.display }}</cs-table-cell>
                      <cs-table-cell align-right>
                        @if (detectedBarRows.bar.lowConfidence) {
                          <cs-tooltip class="low-confidence-tooltip" iconType="warning"
                            [text]="LL.equipmentScanner.lowConfidenceTooltip()"></cs-tooltip>
                          }
                      </cs-table-cell>
                      <cs-table-cell align-right>
                        <cs-tooltip [text]="LL.equipmentScanner.confidenceTooltip()" [hasCustomTrigger]="true">
                          <cs-badge tooltip-trigger [label]="detectedBarRows.bar.confidencePct"></cs-badge>
                        </cs-tooltip>
                      </cs-table-cell>
                      </cs-table-row>
                      </cs-table>
                      </cs-form-field>
                      }
              @if (detectedBarRows.collar) {
                <cs-form-field [label]="LL.equipmentScanner.collarGroup()">
                  <cs-table class="detected-table">
                    <cs-table-row>
                      <cs-table-cell><cs-checkbox [ariaLabel]="detectedBarRows.collar.display | untranslated"
                        [(checked)]="detectedBarRows.collar.checked"></cs-checkbox></cs-table-cell>
                      <cs-table-cell>{{ detectedBarRows.collar.display }}</cs-table-cell>
                      <cs-table-cell align-right>
                        @if (detectedBarRows.collar.lowConfidence) {
                          <cs-tooltip class="low-confidence-tooltip" iconType="warning"
                            [text]="LL.equipmentScanner.lowConfidenceTooltip()"></cs-tooltip>
                          }
                      </cs-table-cell>
                      <cs-table-cell align-right>
                        <cs-tooltip [text]="LL.equipmentScanner.confidenceTooltip()" [hasCustomTrigger]="true">
                          <cs-badge tooltip-trigger [label]="detectedBarRows.collar.confidencePct"></cs-badge>
                        </cs-tooltip>
                      </cs-table-cell>
                      </cs-table-row>
                      </cs-table>
                      </cs-form-field>
                      }
              @if (detectedBarRows.plates.length) {
              <cs-form-field [label]="LL.equipmentScanner.platesGroup()">
                <cs-table class="detected-table">
                  @for (item of detectedBarRows.plates; track $index) {
                      <cs-table-row>
                        <cs-table-cell><cs-checkbox [ariaLabel]="item.display | untranslated"
                          [(checked)]="item.checked"></cs-checkbox></cs-table-cell>
                        <cs-table-cell>{{ item.display }}</cs-table-cell>
                        <cs-table-cell align-right>
                          @if (item.lowConfidence) {
                            <cs-tooltip class="low-confidence-tooltip" iconType="warning"
                              [text]="LL.equipmentScanner.lowConfidenceTooltip()"></cs-tooltip>
                            }
                        </cs-table-cell>
                        <cs-table-cell align-right>
                          <cs-tooltip [text]="LL.equipmentScanner.confidenceTooltip()" [hasCustomTrigger]="true">
                            <cs-badge tooltip-trigger [label]="item.confidencePct"></cs-badge>
                          </cs-tooltip>
                        </cs-table-cell>
                        </cs-table-row>
                        }
                  </cs-table>
                  </cs-form-field>
                  }
                  </cs-item-list>
          } @else {
            <cs-table class="detected-table">
              @for (item of detectedPrimaryRows; track $index) {
                <cs-table-row>
                  <cs-table-cell><cs-checkbox [ariaLabel]="item.display | untranslated"
                          [(checked)]="item.checked"></cs-checkbox></cs-table-cell>
                  <cs-table-cell>{{ item.display }}</cs-table-cell>
                  <cs-table-cell align-right>
                    @if (item.lowConfidence) {
                      <cs-tooltip class="low-confidence-tooltip" iconType="warning"
                        [text]="LL.equipmentScanner.lowConfidenceTooltip()"></cs-tooltip>
                      }
                  </cs-table-cell>
                  <cs-table-cell align-right>
                    <cs-tooltip [text]="LL.equipmentScanner.confidenceTooltip()" [hasCustomTrigger]="true">
                      <cs-badge tooltip-trigger [label]="item.confidencePct"></cs-badge>
                    </cs-tooltip>
                  </cs-table-cell>
                  </cs-table-row>
                  }
            </cs-table>
            <!-- microloading add-on plates for Stack/Fixed, shown as a distinct group (#3523) -->
            @if (detectedMicroPlateRows.length) {
              <cs-form-field [label]="LL.equipmentScanner.microPlatesGroup()">
                <cs-table class="detected-table">
                  @for (item of detectedMicroPlateRows; track $index) {
                    <cs-table-row>
                      <cs-table-cell><cs-checkbox [ariaLabel]="item.display | untranslated"
                          [(checked)]="item.checked"></cs-checkbox></cs-table-cell>
                      <cs-table-cell>{{ item.display }}</cs-table-cell>
                      <cs-table-cell align-right>
                        @if (item.lowConfidence) {
                          <cs-tooltip class="low-confidence-tooltip" iconType="warning"
                            [text]="LL.equipmentScanner.lowConfidenceTooltip()"></cs-tooltip>
                          }
                      </cs-table-cell>
                      <cs-table-cell align-right>
                        <cs-tooltip [text]="LL.equipmentScanner.confidenceTooltip()" [hasCustomTrigger]="true">
                          <cs-badge tooltip-trigger [label]="item.confidencePct"></cs-badge>
                        </cs-tooltip>
                      </cs-table-cell>
                      </cs-table-row>
                      }
                </cs-table>
              </cs-form-field>
              }
          }
        </cs-card-content>
        </cs-card>

      <!-- 3. Apply-mode card — only relevant when applying onto an existing list -->
      @if (draft) {
      <cs-card class="apply-mode-card">
        <cs-card-title>{{ LL.equipmentScanner.applyModeHeader() }}</cs-card-title>
        <cs-card-content>
          <cs-radio-group [(selected)]="selectedApplyMode">
            <cs-radio value="replace">
              {{ LL.equipmentScanner.applyMode.replace() }}
              <cs-note>{{ LL.equipmentScanner.applyMode.replaceHelp() }}</cs-note>
            </cs-radio>
            <cs-radio value="merge">
              {{ LL.equipmentScanner.applyMode.merge() }}
              <cs-note>{{ LL.equipmentScanner.applyMode.mergeHelp() }}</cs-note>
            </cs-radio>
            <cs-radio value="append">
              {{ LL.equipmentScanner.applyMode.append() }}
              <cs-note>{{ LL.equipmentScanner.applyMode.appendHelp() }}</cs-note>
            </cs-radio>
          </cs-radio-group>
        </cs-card-content>
        </cs-card>
        }

      <!-- 4. Resulting-values card (collapsible) — preview of the draft post-apply.
           Use the total (primary + micro plates) so a Stack/Fixed scan that detected only micro
           plates still shows the drawer (#3523) -->
      @if (resultPreviewTotalCount > 0 && previewDraft) {
        <cs-card class="current-values-card" [foldable]="true" [(expanded)]="currentValuesExpanded">
          <cs-card-title>{{ currentValuesLabel(currentValuesExpanded) }}</cs-card-title>
          <cs-card-content expanded>
            @if (previewDraft.type === EquipmentType.Bar) {
              <cs-item-list>
                @if (currentBarRow) {
                  <cs-form-field [label]="LL.equipmentScanner.barGroup()">
                    <cs-table class="current-values-table">
                      <cs-table-row><cs-table-cell>{{ currentBarRow }}</cs-table-cell></cs-table-row>
                    </cs-table>
                  </cs-form-field>
                }
                @if (currentCollarRow) {
                  <cs-form-field [label]="LL.equipmentScanner.collarGroup()">
                    <cs-table class="current-values-table">
                      <cs-table-row><cs-table-cell>{{ currentCollarRow }}</cs-table-cell></cs-table-row>
                    </cs-table>
                  </cs-form-field>
                }
                @if (currentPlateRows.length) {
                  <cs-form-field [label]="LL.equipmentScanner.platesGroup()">
                    <cs-table class="current-values-table">
                      @for (row of currentPlateRows; track $index) {
                        <cs-table-row><cs-table-cell>{{ row }}</cs-table-cell></cs-table-row>
                      }
                    </cs-table>
                  </cs-form-field>
                }
              </cs-item-list>
            } @else if (previewDraft.type === EquipmentType.Plates) {
              <cs-table class="current-values-table">
                @for (row of currentPlateRows; track $index) {
                  <cs-table-row><cs-table-cell>{{ row }}</cs-table-cell></cs-table-row>
                }
              </cs-table>
            } @else if (previewDraft.type === EquipmentType.Stack) {
              <cs-table class="current-values-table">
                @for (row of currentStackRows; track $index) {
                  <cs-table-row><cs-table-cell>{{ row }}</cs-table-cell></cs-table-row>
                }
              </cs-table>
            } @else if (previewDraft.type === EquipmentType.Fixed) {
              <cs-table class="current-values-table">
                @for (row of currentFixedRows; track $index) {
                  <cs-table-row><cs-table-cell>{{ row }}</cs-table-cell></cs-table-row>
                }
              </cs-table>
            }
            <!-- microloading add-on plates — currentMicroPlateRows is [] for non-Stack/Fixed types,
                 so this renders only under a Stack/Fixed preview, after its primary table (#3523) -->
            @if (currentMicroPlateRows.length) {
              <cs-form-field [label]="LL.equipmentScanner.microPlatesGroup()">
                <cs-table class="current-values-table">
                  @for (row of currentMicroPlateRows; track $index) {
                    <cs-table-row><cs-table-cell>{{ row }}</cs-table-cell></cs-table-row>
                  }
                </cs-table>
              </cs-form-field>
            }
          </cs-card-content>
        </cs-card>
      }

      <!-- 5. Live result summary -->
      <cs-card class="result-summary-card">
        <cs-card-content>
          <span class="summary-after">{{ LL.equipmentScanner.afterApply({ count: resultPreviewTotalCount }) }}</span>
          <cs-note>{{ LL.equipmentScanner.usingChecked({ checked: checkedCount, total: resultItems.length }) }}</cs-note>
        </cs-card-content>
        </cs-card>
        
        </cs-card-list>
  }
</cs-content>

<!-- Footer -->
<!-- Analyze + Retake (all photos taken), stacked vertically — same pattern as meal/label -->
@if (phase === 'camera' && allPhotosTaken) {
  <cs-page-action-buttons class="vertical">
    <cs-button primary [label]="LL.equipmentScanner.analyzeButton()" (click)="analyzeAsync()"></cs-button>
    <cs-button secondary [label]="LL.equipmentScanner.retakeButton()" (click)="retakeAllPhotos()"></cs-button>
  </cs-page-action-buttons>
}

@if (phase === 'results') {
  <cs-page-action-buttons class="vertical">
    <cs-button primary [label]="LL.common.buttons.apply()" [disabled]="isApplyDisabled" (click)="confirm()"></cs-button>
    <cs-button secondary [label]="LL.equipmentScanner.retakeButton()" (click)="retakeFromResults()"></cs-button>
  </cs-page-action-buttons>
}
